import React, { useState, useEffect } from 'react';
import { elementsAPI } from '../services/api';
import { Element } from '../types';
import ElementCard from '../components/ElementCard';
// @ts-ignore
import CreateElementModal from '../components/CreateElementModal';
import ElementSidePanel from '../components/ElementSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';
import { useDataCache } from '../hooks/useDataCache';
import { usePageLoading } from '../hooks/usePageLoading';
import LoadingOverlay from '../components/LoadingOverlay';
import '../styles/Elements.css';

const Elements: React.FC = () => {
  // Use caching hook for elements data
  const {
    data: elements = [],
    isLoading: elementsLoading,
    error: elementsError,
    fetchData: fetchElements,
    preloadData: preloadElements
  } = useDataCache<Element[]>({
    cacheKey: 'elements',
    fetchFunction: elementsAPI.getAll,
    cacheDuration: 5 * 60 * 1000, // 5 minutes
    autoRefresh: true,
    preloadCondition: () => true // Always preload elements
  });

  const [filteredElements, setFilteredElements] = useState<Element[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest'>('date-created');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('summary');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showElementPanel, setShowElementPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [updatedElementIds, setUpdatedElementIds] = useState<Set<string>>(new Set());
  const [dataVersion, setDataVersion] = useState(0); // Add version to force remounts

  // Use page loading hook - wait for elements data to load
  const { isLoading: pageLoading } = usePageLoading({
    dependencies: [
      elements !== undefined, // Elements data has been fetched
      !elementsLoading // Elements are no longer loading
    ],
    onLoadComplete: () => {
      console.log('✅ Elements page loaded successfully');
    }
  });


  useEffect(() => {
    // Filter and sort elements
    if (Array.isArray(elements || [])) {
      let filtered = (elements || []).filter(element =>
        element && element.name && element.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Sort the filtered elements
      filtered = filtered.sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'date-created':
            return new Date(b.createdAt || b._id).getTime() - new Date(a.createdAt || a._id).getTime();
          case 'date-created-oldest':
            return new Date(a.createdAt || a._id).getTime() - new Date(b.createdAt || b._id).getTime();
          default:
            return 0;
        }
      });

      setFilteredElements(filtered);
    }
  }, [elements, searchTerm, sortBy]);



  const handleElementCreated = (newElement: Element) => {
    setShowCreateModal(false);
    
    // Force refresh all elements to get updated data
    const refreshAllElements = async () => {
      try {
        await fetchElements(true); // Force refresh
        setDataVersion(prev => prev + 1);
      } catch (error) {
        console.error('Error refreshing elements:', error);
      }
    };
    
    refreshAllElements();
  };

  const handleUpdateElement = (updatedElement: Element) => {
    // Force refresh all elements to get updated data
    const refreshAllElements = async () => {
      try {
        await fetchElements(true); // Force refresh
        setDataVersion(prev => prev + 1);
      } catch (error) {
        console.error('Error refreshing elements:', error);
      }
    };
    
    refreshAllElements();
  };

  const handleDeleteElement = (elementId: string) => {
    // Force refresh all elements to get updated data
    const refreshAllElements = async () => {
      try {
        await fetchElements(true); // Force refresh
      } catch (error) {
        console.error('Error refreshing elements:', error);
      }
    };
    
    refreshAllElements();
  };



  const handleOpenPreviewPanel = (elementIds: string[]) => {
    setShowElementPreview(true);
  };

  return (
    <div className="elements">
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={pageLoading} message="Loading elements..." />
      
      {/* Header */}
      <div className="elements-header">
        <div>
          <h1 className="elements-title">Elements</h1>
          <p className="elements-subtitle">Manage and organize your elements</p>
        </div>
        <div className="elements-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Element
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest')}
            className="sort-select"
          >
            <option value="date-created">Newest First</option>
            <option value="date-created-oldest">Oldest First</option>
            <option value="name-asc">A to Z</option>
            <option value="name-desc">Z to A</option>
          </select>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        {/* Left side - Filter dropdowns */}
        <div className="filter-left">
          <button
            className="btn btn--clear-filters"
            onClick={() => {
              setSearchTerm('');
            }}
          >
            Clear Filters
          </button>
        </div>

        {/* Right side - Tab selection */}
        <div className="filter-right">
          <div className="filter-group">
            <label htmlFor="tab-filter" className="filter-label">Default Tab:</label>
            <select
              id="tab-filter"
              value={globalTabSelection}
              onChange={(e) => setGlobalTabSelection(e.target.value)}
              className="filter-select tab-select"
            >
              <option value="summary">Summary</option>
              <option value="features">Features</option>
              <option value="attributes">Attributes</option>
              <option value="linkedCharacters">Linked Characters</option>
            </select>
          </div>
        </div>
      </div>

      {/* Element Cards */}
      <div className="card-container" id="results-container">
        {Array.isArray(filteredElements) && filteredElements.length > 0 ? (
          filteredElements.map(element => {
            // Create a hash of related character data to force remount when data changes
            const relatedCharsHash = [
              element.weaponsOf?.length || 0,
              element.armorOf?.length || 0,
              element.itemsOf?.length || 0,
              element.speciesOf?.length || 0,
              element.genderOf?.length || 0,
              element.beliefsOf?.length || 0,
              element.familyOf?.length || 0,
              element.affiliationsOf?.length || 0,
              element.abilitiesOf?.length || 0
            ].join('-');
            
            return (
              <ElementCard
                key={`${element._id}-${dataVersion}-${relatedCharsHash}`}
                element={element}
                onUpdate={handleUpdateElement}
                onDelete={handleDeleteElement}

                onOpenPreviewPanel={handleOpenPreviewPanel}
                defaultTab={globalTabSelection}
              />
            );
          })
        ) : (
          <div className="no-results">
            <p>No elements found.</p>
          </div>
        )}
      </div>

      {/* Create Element Modal */}
      {showCreateModal && (
        <CreateElementModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onElementCreated={handleElementCreated}
        />
      )}

      {/* Element Side Panel */}
      {showElementPanel && selectedElement && (
        <ElementSidePanel
          isOpen={showElementPanel}
          element={selectedElement}
          onClose={() => {
            setShowElementPanel(false);
            setSelectedElement(null);
          }}
          onUpdate={handleUpdateElement}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={() => setShowElementPreview(false)}
        />
      )}
    </div>
  );
};

export default Elements; 